// @ts-nocheck
import React, { Ref, useState, useEffect, useRef } from "react";
import {
	Box,
	Button,
	Checkbox as Check,
	Flex,
	Table,
	TableContainer,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useColorModeValue,
	Tooltip,
} from "@chakra-ui/react";

import { BsCaretDownFill, BsCaretUpFill } from "react-icons/bs";

import {
	useTable,
	usePagination,
	useSortBy,
	useRowSelect,
	useGlobalFilter,
	useResizeColumns,
} from "react-table";
import { GlobalFilter } from "./GlobalFilter";
import Checkbox from "./Checkbox";

type Column = {
	Header: string;
	accessor: string;
};

type Data = {
	[key: string]: any;
};

type Props = {
	column?: Column[];
	data?: Data[];
	renderCheckBox?: boolean; // Controls whether selection controls are shown
	singleSelect?: boolean; // If true, only one row can be selected (now using checkbox instead of radio)
	renderSerialNo?: boolean;
	onRowSelect?: any;
	showSelectedRowCount?: boolean;
	isShowSearch?: boolean;
	headerColor?: string;
	headerTextColor?: string;
	paginationbg?: string;
	showItemsAdded?: boolean;
	zeroScreenComponent?: any;
	borderColor?: any;
	placeholderText?: string;
	isPagination?: boolean;
	isStyled?: boolean;
	bgColor?: string;
	rowColor?: string;
	dividerColor?: string; // Normal divider color
	selectedDividerColor?: string; // Divider color for selected rows
	showDivider?: boolean;
	checkedRows?: any[]; // Array of rows to be pre-selected
	useSmallPageSize?: boolean; // If true, uses smaller page size (5 instead of 10)
	showPageSizeOptions?: boolean; // If true, shows page size option buttons
	getTooltipText?: any; // Function to get tooltip text for checkboxes
	isSpecificCheckboxDisabled?: any; // Function to determine if a specific checkbox or row is disabled
	uniqueKeyName?: string;
	rowPaddingY?: string;
	stickyLeftCount?: number; // how many left columns to freeze (includes Sl. No.)
	stickyRightCount?: number; // how many right columns to freeze
	stickyZIndex?: number; // optional z-index for sticky cells (default 2)
	stickyBg?: string;
	isServerPagination?: boolean; // default false
	pageIndex?: number; // controlled index (0-based)
	pageSize?: number; // controlled size
	pageCount?: number; // total pages (Math.ceil(totalRows/pageSize))
	onPageChange?: (nextPageIndex: number) => void;
	onPageSizeChange?: (nextSize: number) => void;
	hideColumnHeaders?: boolean;
};

export const GenericTable: React.FC<Props> = ({
	column,
	data,
	renderCheckBox,
	singleSelect = false, // Default to multi-select mode
	renderSerialNo = true,
	onRowSelect,
	showSelectedRowCount,
	isShowSearch,
	headerColor,
	headerTextColor,
	paginationbg,
	showItemsAdded,
	zeroScreenComponent,
	borderColor,
	placeholderText,
	isPagination = true,
	isStyled,
	bgColor,
	rowColor,
	dividerColor = "#E5EAEE", // Default divider color
	selectedDividerColor = "#E5EAEE", // Default selected divider color
	showDivider = false,
	checkedRows = [], // Array of rows to be pre-selected
	useSmallPageSize = false, // Default to false (use 10 as default)
	showPageSizeOptions = true, // Default to true (show page size options)
	getTooltipText,
	isSpecificCheckboxDisabled = false,
	uniqueKeyName = "id",
	rowPaddingY,
	stickyLeftCount = 0,
	stickyRightCount = 0,
	stickyZIndex = 2,
	stickyBg,
	isServerPagination = false,
	pageIndex: controlledPageIndex,
	pageSize: controlledPageSize,
	pageCount: controlledPageCount,
	onPageChange,
	onPageSizeChange,
	hideColumnHeaders = false,
}) => {
	const memoColumns = React.useMemo(() => column ?? [], [column]);
	const memoData = React.useMemo(() => data ?? [], [data]);

	const localInitialPageSize = useSmallPageSize ? 5 : 10;
	const [localPageIndex, setLocalPageIndex] = useState(0);
	const [localPageSize, setLocalPageSize] = useState(localInitialPageSize);

	const color = useColorModeValue("#000", "#000");

	// Track if selection update is in progress to prevent cascading updates
	const isUpdatingSelection = useRef(false);
	// Track previous checked rows for comparison
	const prevCheckedRowsRef = useRef(checkedRows);
	// Track manually selected/deselected rows
	const manuallyDeselectedRef = useRef(new Set());
	const manuallySelectedRef = useRef(new Set());

	const [selectionState, setSelectionState] = useState({});

	// Create a custom selection hook to handle selection without flicker
	const selectionHook = (hooks) => {
		hooks.getToggleRowSelectedProps = (row) => ({
			onChange: (e) => {
				if (singleSelect) {
					const newSelectionState = e.target.checked ? { [row.id]: true } : {};
					setSelectionState(newSelectionState);
				} else {
					setSelectionState((prevState) => ({
						...prevState,
						[row.id]: e.target.checked,
					}));
				}
			},
			checked: !!selectionState[row.id],
		});

		hooks.stateReducers.push((state, action) => {
			if (action.type === "SET_SELECTED_ROWS") {
				return {
					...state,
					selectedRowIds: selectionState,
				};
			}
			return state;
		});
	};

	const {
		getTableProps,
		getTableBodyProps,
		state,
		headerGroups,
		page,
		nextPage,
		previousPage,
		canNextPage,
		canPreviousPage,
		prepareRow,
		pageCount,
		pageOptions,
		gotoPage,
		selectedFlatRows,
		setPageSize,
		setGlobalFilter,
		toggleRowSelected,
		toggleAllRowsSelected,
	} = useTable(
		{
			columns: memoColumns,
			data: memoData,
			// IMPORTANT: if server mode, make pagination manual & controlled
			manualPagination: isServerPagination,
			pageCount: isServerPagination ? controlledPageCount ?? 0 : undefined,

			initialState: { selectedRowIds: {}, pageSize: localInitialPageSize },
			autoResetPage: false,
			// Only control state in SERVER mode
			useControlledState: (s) => ({
				...s,
				pageIndex: isServerPagination
					? controlledPageIndex ?? s.pageIndex
					: localPageIndex,
				pageSize: isServerPagination
					? controlledPageSize ?? s.pageSize
					: localPageSize,
			}),
		},
		useResizeColumns,
		useGlobalFilter,
		useSortBy,
		usePagination,
		useRowSelect,
		selectionHook
	);

	const { globalFilter, pageIndex, pageSize } = state;

	const [columnSpan, setColumnSpan] = useState<number>(
		column === undefined ? 100 : column?.length + 1
	);

	const effectivePageIndex = state.pageIndex;
	const effectivePageSize = state.pageSize;
	const totalPages = isServerPagination
		? controlledPageCount ?? 0
		: pageOptions.length;

	// Pagination range calculation
	const paginationRange = 2; // number of page numbers to display before and after the current page

	const paginationStart = Math.max(0, effectivePageIndex - paginationRange);
	const paginationEnd = Math.min(
		totalPages,
		effectivePageIndex + paginationRange + 1
	);

	const handleSetPageSize = (size: number) => {
		if (isServerPagination) {
			onPageChange?.(0);
			onPageSizeChange?.(size);
		} else {
			setLocalPageSize(size);
			setLocalPageIndex(0);
		}
	};

	const goTo = (idx: number) => {
		if (isServerPagination) onPageChange?.(idx);
		else setLocalPageIndex(idx);
	};

	// Handle checkbox changes
	const handleCheckboxChange = (rowKey, isChecked) => {
		isUpdatingSelection.current = true;

		if (singleSelect) {
			// For single select mode, update the entire selection state at once
			const newSelectionState = isChecked ? { [rowKey]: true } : {};
			setSelectionState(newSelectionState);
		} else {
			// For multi-select mode, update just the changed row
			setSelectionState((prevState) => ({
				...prevState,
				[rowKey]: isChecked,
			}));
		}

		// Track manually selected/deselected rows for sync with checkedRows
		if (isChecked) {
			manuallySelectedRef.current.add(rowKey);
			manuallyDeselectedRef.current.delete(rowKey);
		} else {
			manuallyDeselectedRef.current.add(rowKey);
			manuallySelectedRef.current.delete(rowKey);
		}

		isUpdatingSelection.current = false;
	};

	const headerRef = useRef<HTMLTableRowElement | null>(null);
	const [leftOffsets, setLeftOffsets] = useState<number[]>([]);
	const [rightOffsets, setRightOffsets] = useState<number[]>([]);

	const computeOffsets = () => {
		const row = headerRef.current;
		if (!row) return;

		// Header cells: first cell is "Sl. No.", then your dynamic columns
		const cells = Array.from(row.children) as HTMLElement[];
		if (!cells.length) return;

		// widths
		const widths = cells.map((c) => c.getBoundingClientRect().width);

		// LEFT offsets (for first N sticky cells)
		const left: number[] = [];
		let acc = 0;
		for (let i = 0; i < Math.min(stickyLeftCount, widths.length); i++) {
			left.push(acc);
			acc += widths[i];
		}

		// RIGHT offsets (for last M sticky cells)
		const right: number[] = [];
		let racc = 0;
		for (let i = 0; i < Math.min(stickyRightCount, widths.length); i++) {
			const idxFromRight = widths.length - 1 - i;
			right[idxFromRight] = racc;
			racc += widths[idxFromRight];
		}

		setLeftOffsets(left);
		setRightOffsets(right);
	};

	const stickyStyleForIndex = (
		headerCellIndexZeroBased: number, // 0 => Sl. No., 1 => first data column, etc
		isHeader: boolean
	) => {
		const style: React.CSSProperties = {};
		const bg = (isHeader ? headerColor : undefined) || "#fff";

		// Left sticky?
		if (headerCellIndexZeroBased < stickyLeftCount) {
			style.position = "sticky";
			style.left = leftOffsets[headerCellIndexZeroBased] ?? 0;
			style.zIndex = stickyZIndex;
			style.background = bg;
		}

		// Right sticky?
		const total = (headerGroups?.[0]?.headers.length ?? 0) + 1; // +1 for Sl.No.
		const lastIndexStart = total - stickyRightCount;
		if (stickyRightCount > 0 && headerCellIndexZeroBased >= lastIndexStart) {
			style.position = "sticky";
			style.right = rightOffsets[headerCellIndexZeroBased] ?? 0;
			style.zIndex = stickyZIndex;
			style.background = bg;
		}

		return style;
	};

	useEffect(() => {
		computeOffsets();
		const onResize = () => computeOffsets();
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
		// re-run when columns or header color/size toggles might change widths
	}, [
		headerGroups,
		headerColor,
		headerTextColor,
		showDivider,
		dividerColor,
		data,
	]);

	// Call onRowSelect only when the selected rows truly change
	const prevSelectedRef = useRef<Data[]>([]);
	useEffect(() => {
		if (Object.keys(selectionState).length === 0) return;

		const newSelectedRows = data
			.filter((row) => selectionState[row[uniqueKeyName]])
			.map((row) => row);

		const prev = JSON.stringify(prevSelectedRef.current);
		const curr = JSON.stringify(newSelectedRows);

		if (onRowSelect && !isUpdatingSelection.current && prev !== curr) {
			prevSelectedRef.current = newSelectedRows;
			onRowSelect(newSelectedRows);
		}
	}, [selectionState, data, uniqueKeyName]);

	useEffect(() => {
		if (!checkedRows || checkedRows.length === 0) return;

		const initialSelection: Record<string, boolean> = {};
		checkedRows.forEach((row) => {
			initialSelection[row[uniqueKeyName]] = true;
		});

		setSelectionState((prev) => {
			const prevStr = JSON.stringify(prev);
			const nextStr = JSON.stringify(initialSelection);
			return prevStr === nextStr ? prev : initialSelection;
		});
	}, [checkedRows, uniqueKeyName]);

	useEffect(() => {
		const len = checkedRows?.length ?? 0;
		if (len === 0) {
			setSelectionState((prev) => (Object.keys(prev).length ? {} : prev));
			manuallySelectedRef.current.clear();
			manuallyDeselectedRef.current.clear();
		}
	}, [checkedRows]);

	// Function to render page size buttons based on useSmallPageSize prop
	const renderPageSizeButtons = () => {
		if (!showPageSizeOptions) return null;

		// If using small page size, show smaller options
		if (useSmallPageSize) {
			return (
				<React.Fragment>
					<Button
						className="acePageSizeBtn"
						id="ace_page_size_btn"
						size="xs"
						bg={effectivePageSize === 5 ? paginationbg || "#0087EE" : undefined}
						color={effectivePageSize === 5 ? "#fff" : "rgba(94, 98, 120, 1)"}
						_hover={{ opacity: ".8" }}
						onClick={() => handleSetPageSize(5)}
						mr="0.5em"
					>
						5
					</Button>
					<Button
						className="acePageSizeBtn"
						id="ace_page_size_btn"
						color={effectivePageSize === 10 ? "#fff" : "rgba(94, 98, 120, 1)"}
						bg={
							effectivePageSize === 10 ? paginationbg || "#0087EE" : undefined
						}
						_hover={{ opacity: ".8" }}
						size="xs"
						onClick={() => handleSetPageSize(10)}
						mr="0.5em"
					>
						10
					</Button>
					<Button
						className="acePageSizeBtn"
						id="ace_page_size_btn"
						color={effectivePageSize === 20 ? "#fff" : "rgba(94, 98, 120, 1)"}
						bg={
							effectivePageSize === 20 ? paginationbg || "#0087EE" : undefined
						}
						_hover={{ opacity: ".8" }}
						size="xs"
						onClick={() => handleSetPageSize(20)}
						mr="0.5em"
					>
						20
					</Button>
				</React.Fragment>
			);
		}

		// Default page size options
		return (
			<React.Fragment>
				<Button
					className="acePageSizeBtn"
					id="ace_page_size_btn"
					size="xs"
					bg={effectivePageSize === 10 ? paginationbg || "#0087EE" : undefined}
					color={effectivePageSize === 10 ? "#fff" : "rgba(94, 98, 120, 1)"}
					_hover={{ opacity: ".8" }}
					onClick={() => handleSetPageSize(10)}
					mr="0.5em"
				>
					10
				</Button>
				<Button
					className="acePageSizeBtn"
					id="ace_page_size_btn"
					color={effectivePageSize === 20 ? "#fff" : "rgba(94, 98, 120, 1)"}
					bg={effectivePageSize === 20 ? paginationbg || "#0087EE" : undefined}
					_hover={{ opacity: ".8" }}
					size="xs"
					onClick={() => handleSetPageSize(20)}
					mr="0.5em"
				>
					20
				</Button>
				<Button
					className="acePageSizeBtn"
					id="ace_page_size_btn"
					color={effectivePageSize === 50 ? "#fff" : "rgba(94, 98, 120, 1)"}
					bg={effectivePageSize === 50 ? paginationbg || "#0087EE" : undefined}
					_hover={{ opacity: ".8" }}
					size="xs"
					onClick={() => handleSetPageSize(50)}
					mr="0.5em"
				>
					50
				</Button>
			</React.Fragment>
		);
	};

	return (
		<Box>
			{page.length <= 0 ? (
				zeroScreenComponent || (
					<Text textAlign="center">No data available!</Text>
				)
			) : (
				<Box overflow="auto">
					<Flex justifyContent={"space-between"} alignItems="center">
						{isShowSearch && (
							<GlobalFilter
								filter={globalFilter}
								setFilter={setGlobalFilter}
								placeholderText={placeholderText}
							/>
						)}
						{showSelectedRowCount && (
							<Text textStyle="primary.secondaryText">
								{Object.keys(selectionState).length}&nbsp; Selected out of
								&nbsp;
								{data?.length}
							</Text>
						)}
						{showItemsAdded &&
							(Object.keys(selectionState).length > 0 ? (
								<Text textStyle="primary.secondaryText">
									{Object.keys(selectionState).length}&nbsp; Selected out of
									&nbsp;
									{data?.length}
								</Text>
							) : (
								<Text
									textStyle="primary.secondaryText"
									fontWeight="semibold"
									color="black"
								>
									Items added&nbsp; ({data?.length})
								</Text>
							))}
					</Flex>

					<TableContainer borderRadius="lg">
						<Table
							{...getTableProps()}
							bg={bgColor ? "rgba(0, 135, 238, 0.1)" : "transparent"}
							size={"sm"}
						>
							{!hideColumnHeaders && (
								<Thead>
									{headerGroups?.map((headerGroup, headerIndex) => (
										<Tr
											{...headerGroup.getHeaderGroupProps()}
											key={headerIndex}
											ref={headerIndex === 0 ? headerRef : undefined}
										>
											{renderSerialNo && (
												<Th
													bgColor={
														headerColor ? headerColor : "rgba(0, 135, 238, 0.1)"
													}
													border="none"
													borderBottom={
														showDivider ? `1px solid ${dividerColor}` : "none"
													}
													borderRadius={"0.375rem"}
													borderBottomRadius={".375rem"}
													borderBottomRightRadius={".375rem"}
													borderRightRadius={".375rem"}
													textTransform="none"
													color={headerTextColor || "#7F7F7F"}
													fontFamily="Rubik"
													fontWeight={"medium"}
													textAlign={"left"}
													fontSize={"14px"}
													py={showDivider ? "0.5em" : "1rem"}
													px="8px"
													pl="24px"
													letterSpacing={0}
													style={stickyStyleForIndex(0, true)}
												>
													Sl. No.
												</Th>
											)}
											{headerGroup.headers?.map((column, index) => (
												<Th
													bgColor={
														headerColor ? headerColor : "rgba(0, 135, 238, 0.1)"
													}
													borderBottom={
														showDivider ? `1px solid ${dividerColor}` : "none"
													}
													textTransform="none"
													{...column.getHeaderProps(
														column.getSortByToggleProps()
													)}
													color={
														headerTextColor ??
														(showDivider ? "#7F7F7F" : "black")
													}
													fontFamily="Rubik"
													fontWeight={"medium"}
													textAlign={"left"}
													fontSize={showDivider ? "14px" : "16px"}
													py={showDivider ? "0.5em" : "1rem"}
													px="8px"
													letterSpacing={0}
													key={index}
													style={stickyStyleForIndex(index + 1, true)}
												>
													<Box
														p="0.5em 0.3em"
														fontFamily="Rubik"
														fontWeight="semibold"
														display={"flex"}
														flexDirection="row"
														alignItems="center"
														gap="1rem"
													>
														<span>{column.render("Header")}</span>
														{!column.disableSortBy && ( // Check if sorting is enabled for the column
															<span
																onClick={() => column.toggleSortBy()}
																style={{ cursor: "pointer" }}
															>
																{column.isSorted ? (
																	column.isSortedDesc ? (
																		<BsCaretDownFill
																			size="13px"
																			color="#0087EE"
																		/>
																	) : (
																		<BsCaretUpFill
																			size="13px"
																			color="#0087EE"
																		/>
																	)
																) : (
																	<Box>
																		<BsCaretUpFill
																			size="13px"
																			color="#0087EE"
																		/>
																		<BsCaretDownFill size="13px" color="gray" />
																	</Box>
																)}
															</span>
														)}
													</Box>
												</Th>
											))}
										</Tr>
									))}
								</Thead>
							)}

							<Tbody {...getTableBodyProps()} bg="#fff">
								{page?.length > 0 ? (
									page.map((row, index) => {
										prepareRow(row);
										// Check if this row is selected based on our controlled selection state
										const rowKey = row.original[uniqueKeyName]; // Or another unique field
										const isSelected = !!selectionState[rowKey];
										const dividerBorder = showDivider
											? `1px solid ${
													isSelected ? selectedDividerColor : dividerColor
											  }`
											: "none";

										return (
											<Tr
												{...row.getRowProps()}
												backgroundColor={isSelected ? rowColor : "transparent"}
												key={row?.id || index}
											>
												{renderSerialNo && (
													<Td
														pl="24px"
														border={
															!isStyled
																? borderColor
																	? `1px solid ${borderColor}`
																	: "1px solid rgba(0, 135, 238, 0.2)"
																: "none"
														}
														paddingX="5px"
														color={color}
														style={stickyStyleForIndex(0, false)}
														borderBottom={dividerBorder}
													>
														{renderCheckBox ? (
															<Tooltip
																label={
																	getTooltipText ? getTooltipText(row) : ""
																}
															>
																<Flex alignItems="center" gap=".25rem">
																	{/* Use our controlled checkbox */}
																	<Checkbox
																		style={{ size: "12px" }}
																		checked={isSelected}
																		disabled={
																			isSpecificCheckboxDisabled
																				? isSpecificCheckboxDisabled(row)
																				: false
																		}
																		onChange={(e) =>
																			handleCheckboxChange(
																				row.original[uniqueKeyName],
																				e.target.checked
																			)
																		}
																	/>
																	<Text
																		ml="0.4em"
																		p="0.3em"
																		textStyle={"primary.text"}
																		fontWeight="normal"
																	>
																		{index +
																			effectivePageIndex * effectivePageSize +
																			1}
																		.
																	</Text>
																</Flex>
															</Tooltip>
														) : (
															<Text
																ml="0.4em"
																p="0.3em"
																textStyle={"primary.text"}
																fontWeight="normal"
															>
																{index +
																	effectivePageIndex * effectivePageSize +
																	1}
															</Text>
														)}
													</Td>
												)}
												{row.cells?.map((cell, i) => (
													<Td
														{...cell.getCellProps()}
														border={
															!isStyled
																? borderColor
																	? `1px solid ${borderColor}`
																	: "1px solid rgba(0, 135, 238, 0.2)"
																: "none"
														}
														paddingX="5px"
														color={color}
														key={i}
														paddingY={rowPaddingY}
														style={stickyStyleForIndex(i + 1, false)}
														borderBottom={dividerBorder}
													>
														<Text
															ml="0.4em"
															p="0.3em"
															textStyle={"primary.text"}
															fontWeight="normal"
															display={"flex"}
															flexWrap={"wrap"}
															as="span"
														>
															<span>{cell.render("Cell")}</span>
														</Text>
													</Td>
												))}
											</Tr>
										);
									})
								) : (
									<Tr paddingY="0">
										<Td colSpan={columnSpan}>
											<Flex
												width="100%"
												margin="2rem 0"
												justifyContent="center"
												alignItems="center"
												backgroundColor="#fff"
												height="100%"
											>
												{zeroScreenComponent}
											</Flex>
										</Td>
									</Tr>
								)}
							</Tbody>
						</Table>
					</TableContainer>
					{isPagination === true && (
						<Box
							display="flex"
							flexDirection="row"
							justifyContent="space-between"
							flexWrap="wrap"
							my="1em"
							pos={"relative"}
							position={"static"}
						>
							<Flex>
								<Text
									mr="0.5em"
									mt="0.1em"
									fontSize={"14px"}
									fontFamily="Rubik"
									color="rgba(94, 98, 120, 1)"
									fontWeight="semibold"
								>
									Page {effectivePageIndex + 1} of {totalPages}
								</Text>
								{/* Conditionally render page size buttons */}
								{renderPageSizeButtons()}
							</Flex>
							<Flex>
								<Button
									className="acePaginationGotoFirstPageBtn"
									id="ace_pagination_goto_first_page_btn"
									variant="ghost"
									color="#7E8299"
									_hover={{ opacity: ".8" }}
									size="xs"
									ml="0.5em"
									type="button"
									onClick={() => goTo(0)}
									disabled={effectivePageIndex === 0}
								>
									{"<"}
								</Button>

								{Array.from(
									{ length: paginationEnd - paginationStart },
									(_, i) => {
										const p = paginationStart + i;
										return (
											<Button
												className="acePaginationPageIndexBtn"
												id="ace_pagination_page_index_btn"
												size="xs"
												key={i}
												_hover={{ opacity: ".8" }}
												bg={
													effectivePageIndex === p
														? paginationbg || "#0087EE"
														: undefined
												}
												color={effectivePageIndex === p ? "#fff" : undefined}
												onClick={() => goTo(p)}
												ml="0.5em"
												type="button"
											>
												{p + 1}
											</Button>
										);
									}
								)}

								<Button
									className="acePaginationGotoLastPageBtn"
									id="ace_pagination_goto_last_page_btn"
									size="xs"
									ml="0.5em"
									variant="ghost"
									color="#7E8299"
									_hover={{ opacity: ".8" }}
									onClick={() => goTo(Math.max(0, totalPages - 1))}
									disabled={effectivePageIndex >= Math.max(0, totalPages - 1)}
									type="button"
								>
									{">"}
								</Button>
							</Flex>
						</Box>
					)}
				</Box>
			)}
		</Box>
	);
};
